import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { categories } from "../../mocks/categories";
import { getProductUrl } from "../../lib/slug";
import { useCompanies } from "../../hooks/useCompanyData";
import { useNavbarData } from "../../hooks/useNavbarData";

const Navbar = () => {

  // Dynamic data from Supabase (auto-refresh after admin posts)
  const { companies } = useCompanies();
  const { categoryCounts, categoryProducts, companyProducts, companyProductCounts, companiesByCategory, fetchProductsForCategory, fetchProductsForCompany, fetchCompaniesByCategory, prefetchAllCategories } = useNavbarData();

  const KOREA_CARBON_NANO_LOGO = "https://storage.readdy-site.link/project_files/5f2724b2-0dd8-4526-ab2c-c1f8f3f4c6d1/ff2930e2-71a8-4589-bb2f-65a564d9509d_---.png?v=e95b8b61ae78d1ec4a164b9ad07adef2";

  const [isScrolled, setIsScrolled] = useState(false);
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [companyFlyoutOpen, setCompanyFlyoutOpen] = useState(false);
  const [hoveredSubId, setHoveredSubId] = useState<string | null>(null);
  const [hoveredSubCatId, setHoveredSubCatId] = useState<string | null>(null);
  const [hoveredCompanyId, setHoveredCompanyId] = useState<string | null>(null);
  // Company flyout category/subcategory hover (mirrors products flyout)
  const [companyCatId, setCompanyCatId] = useState<string | null>(null);
  const [companySubCatId, setCompanySubCatId] = useState<string | null>(null);
  const companySubCatTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [activitiesFlyoutOpen, setActivitiesFlyoutOpen] = useState(false);
  const activitiesFlyoutTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [introFlyoutOpen, setIntroFlyoutOpen] = useState(false);
  const introFlyoutTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileIntroAccordion, setMobileIntroAccordion] = useState(false);
  const [mobileCompanyAccordion, setMobileCompanyAccordion] = useState<string | null>(null);
  const [mobileProductAccordion, setMobileProductAccordion] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState<"all" | "product" | "company">("all");
  const [searchTypeOpen, setSearchTypeOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchTypeRef = useRef<HTMLDivElement>(null);
  const searchDropdownRef = useRef<HTMLDivElement>(null);
  const flyoutTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const subCatTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const companyFlyoutTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setFlyoutOpen(false);
    setCompanyFlyoutOpen(false);
    setActivitiesFlyoutOpen(false);
    setIntroFlyoutOpen(false);
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchTypeRef.current && !searchTypeRef.current.contains(e.target as Node)) {
        setSearchTypeOpen(false);
      }
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const searchTypes = [
    { value: "all" as const, label: "Tất cả", emoji: "🔍" },
    { value: "product" as const, label: "Sản phẩm", emoji: "📦" },
    { value: "company" as const, label: "Công ty", emoji: "🏢" },
  ];

  const currentSearchType = searchTypes.find((t) => t.value === searchType) ?? searchTypes[0];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    if (searchType === "company") {
      navigate(`/doanh-nghiep?q=${encodeURIComponent(searchQuery)}`);
    } else {
      navigate(`/danh-muc?q=${encodeURIComponent(searchQuery)}&type=${searchType}`);
    }
  };



  // ── Categories flyout handlers ──
  const openFlyout = () => {
    if (flyoutTimeout.current) clearTimeout(flyoutTimeout.current);
    if (companyFlyoutTimeout.current) clearTimeout(companyFlyoutTimeout.current);
    setFlyoutOpen(true);
    setCompanyFlyoutOpen(false);
  };

  const closeFlyout = () => {
    flyoutTimeout.current = setTimeout(() => {
      setFlyoutOpen(false);
      setHoveredSubId(null);
      setHoveredSubCatId(null);
    }, 140);
  };

  const keepFlyout = () => {
    if (flyoutTimeout.current) clearTimeout(flyoutTimeout.current);
  };

  const handleParentHover = (catId: string, catName: string) => {
    if (flyoutTimeout.current) clearTimeout(flyoutTimeout.current);
    setHoveredSubId(catId);
    setHoveredSubCatId(null);
    fetchProductsForCategory(catId, catName);
  };

  const handleSubCatEnter = (subId: string, subName?: string) => {
    if (subCatTimeout.current) clearTimeout(subCatTimeout.current);
    setHoveredSubCatId(subId);
    // Fetch products for this sub-category from Supabase
    fetchProductsForCategory(subId, subName);
  };

  const handleSubCatLeave = () => {
    subCatTimeout.current = setTimeout(() => {
      setHoveredSubCatId(null);
    }, 120);
  };

  const handleSubSubEnter = () => {
    if (subCatTimeout.current) clearTimeout(subCatTimeout.current);
  };

  // ── Company flyout handlers ──
  const openCompanyFlyout = () => {
    if (companyFlyoutTimeout.current) clearTimeout(companyFlyoutTimeout.current);
    if (flyoutTimeout.current) clearTimeout(flyoutTimeout.current);
    setCompanyFlyoutOpen(true);
    setFlyoutOpen(false);
    if (!companyCatId && categories.length > 0) {
      setCompanyCatId(categories[0].id);
      fetchCompaniesByCategory(categories[0].id, categories[0].name);
    }
    // Prefetch all categories in background for instant hover response
    prefetchAllCategories(categories.map((c) => ({ id: c.id, name: c.name })));
  };

  const closeCompanyFlyout = () => {
    companyFlyoutTimeout.current = setTimeout(() => {
      setCompanyFlyoutOpen(false);
      setHoveredCompanyId(null);
    }, 140);
  };

  const keepCompanyFlyout = () => {
    if (companyFlyoutTimeout.current) clearTimeout(companyFlyoutTimeout.current);
  };

  const handleCompanyCatHover = (catId: string, catName: string) => {
    if (companyFlyoutTimeout.current) clearTimeout(companyFlyoutTimeout.current);
    setCompanyCatId(catId);
    setCompanySubCatId(null);
    setHoveredCompanyId(null);
    // Fetch companies dynamically from Supabase by category
    fetchCompaniesByCategory(catId, catName);
  };

  const handleCompanySubCatEnter = (subId: string) => {
    if (companySubCatTimeout.current) clearTimeout(companySubCatTimeout.current);
    setCompanySubCatId(subId);
  };

  const handleCompanySubCatLeave = () => {
    companySubCatTimeout.current = setTimeout(() => {
      setCompanySubCatId(null);
    }, 120);
  };

  const handleCompanyHover = (companyId: string) => {
    if (companyFlyoutTimeout.current) clearTimeout(companyFlyoutTimeout.current);
    setHoveredCompanyId(companyId);
    const company = companies.find((c) => c.id === companyId);
    if (company?.name) fetchProductsForCompany(company.name);
  };

  const openActivitiesFlyout = () => {
    if (activitiesFlyoutTimeout.current) clearTimeout(activitiesFlyoutTimeout.current);
    if (flyoutTimeout.current) clearTimeout(flyoutTimeout.current);
    if (companyFlyoutTimeout.current) clearTimeout(companyFlyoutTimeout.current);
    setActivitiesFlyoutOpen(true);
    setFlyoutOpen(false);
    setCompanyFlyoutOpen(false);
  };

  const closeActivitiesFlyout = () => {
    activitiesFlyoutTimeout.current = setTimeout(() => {
      setActivitiesFlyoutOpen(false);
    }, 140);
  };

  const keepActivitiesFlyout = () => {
    if (activitiesFlyoutTimeout.current) clearTimeout(activitiesFlyoutTimeout.current);
  };

  // ── Intro flyout handlers ──
  const openIntroFlyout = () => {
    if (introFlyoutTimeout.current) clearTimeout(introFlyoutTimeout.current);
    if (flyoutTimeout.current) clearTimeout(flyoutTimeout.current);
    if (companyFlyoutTimeout.current) clearTimeout(companyFlyoutTimeout.current);
    if (activitiesFlyoutTimeout.current) clearTimeout(activitiesFlyoutTimeout.current);
    setIntroFlyoutOpen(true);
    setFlyoutOpen(false);
    setCompanyFlyoutOpen(false);
    setActivitiesFlyoutOpen(false);
  };

  const closeIntroFlyout = () => {
    introFlyoutTimeout.current = setTimeout(() => {
      setIntroFlyoutOpen(false);
    }, 140);
  };

  const keepIntroFlyout = () => {
    if (introFlyoutTimeout.current) clearTimeout(introFlyoutTimeout.current);
  };

  const hoveredCategory = hoveredSubId
    ? (categories.find((c) => c.id === hoveredSubId) ?? null)
    : null;
  const hoveredSubCat = hoveredCategory?.subCategories.find((s) => s.id === hoveredSubCatId) ?? null;
  const hoveredCompany = hoveredCompanyId
    ? (companies.find((c) => c.id === hoveredCompanyId) ?? null)
    : null;

  // Company flyout derived
  const companyCat = companyCatId ? (categories.find((c) => c.id === companyCatId) ?? null) : null;
  const companySubCat = companyCat?.subCategories.find((s) => s.id === companySubCatId) ?? null;

  // Dynamic companies from Supabase for current category
  const supabaseCompanies = companyCatId ? (companiesByCategory[companyCatId] ?? null) : null;
  // Fallback to mock companies if no Supabase data yet
  const filteredCompanies = companyCat
    ? companies.filter((c) => {
        const catName = companyCat.name.toLowerCase();
        const catNameKr = companyCat.nameKr.toLowerCase();
        const mainCat = (c.mainCategoryName ?? "").toLowerCase();
        const mainCatKr = (c.mainCategoryNameKr ?? "").toLowerCase();
        return (
          mainCat.includes(catName) ||
          catName.includes(mainCat.split(" ")[0] ?? "") ||
          mainCatKr.includes(catNameKr) ||
          catNameKr.includes(mainCatKr.split(" ")[0] ?? "")
        );
      })
    : companies;
  const displayCompanies = filteredCompanies.length > 0 ? filteredCompanies : companies;

  const catName = (cat: { name: string }) => cat.name;

  const mainNavItems = [
    {
      id: "intro",
      label: "Giới thiệu",
      hasFlyout: true,
      flyoutType: "intro" as const,
      href: "#",
    },
    {
      id: "new-products",
      label: "Sản Phẩm Mới Hàn Quốc",
      hasFlyout: true,
      flyoutType: "categories" as const,
      href: "/danh-muc",
    },
    {
      id: "companies",
      label: "Doanh nghiệp Hội Viên",
      hasFlyout: true,
      flyoutType: "companies" as const,
      href: "/doanh-nghiep",
    },
    {
      id: "tech-coop",
      label: "Hợp Tác Kỹ Thuật",
      href: "/hop-tac-ky-thuat",
      hasFlyout: false,
      flyoutType: undefined,
    },
    {
      id: "activities",
      label: "Hoạt động & Sự kiện",
      href: "/hoat-dong-hiep-hoi-va-su-kien",
      hasFlyout: false,
      flyoutType: undefined,
    },
    {
      id: "korea-brief",
      label: "Korean Brief",
      href: "/korean-brief",
      hasFlyout: false,
      flyoutType: undefined,
    },
    {
      id: "my-requests",
      label: "Tra Cứu Tư Vấn",
      href: "/tra-cuu-tu-van",
      hasFlyout: false,
      flyoutType: undefined,
    },
  ];

  return (
    <>
      {/* ── Main Navbar ── */}
      <nav
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
          isScrolled
            ? "bg-white shadow-md border-b border-gray-200"
            : "bg-white/97 backdrop-blur-sm shadow-sm"
        }`}
      >
        {/* Logo + Brand Text + Actions row */}
        <div className="border-b border-gray-100 relative overflow-hidden">
          {/* Background image */}
          <div
            className="absolute inset-0 z-0"
            style={{
              backgroundImage: "url('https://storage.readdy-site.link/project_files/5f2724b2-0dd8-4526-ab2c-c1f8f3f4c6d1/31d1a8ab-c8ad-4b41-bc0d-a0544fd1e173_HEADER--CARBON-NANO.png?v=e5216401858424706bc2ad94d72866ce')",
              backgroundSize: "cover",
              backgroundPosition: "center",
              opacity: 0.15,
            }}
          />
          <div className="max-w-[1400px] mx-auto px-3 md:px-6 flex items-center justify-between h-auto min-h-[64px] md:h-[88px] py-2 md:py-0 gap-3 md:gap-4 relative z-10">
            {/* Logo - Korea Carbon Nano */}
            <Link
              to="/"
              className="flex-shrink-0 cursor-pointer flex items-center"
              style={{ height: "88px", textDecoration: "none" }}
            >
              <img
                src={KOREA_CARBON_NANO_LOGO}
                alt="Korea Carbon Nano Logo"
                className={`h-16 md:h-[110px] w-auto max-w-[160px] md:max-w-[420px] object-contain object-left ${location.pathname.startsWith("/korean-brief/") ? "animate-pulse-scale" : ""}`}
                style={{
                  objectPosition: "left center",
                  mixBlendMode: "multiply",
                  background: "transparent",
                  display: "block",
                }}
              />
            </Link>

            {/* Brand Text */}
            <div className="flex flex-col items-center justify-center flex-1 pl-2 md:pl-4 min-w-0">
              <p
                className="text-[13px] sm:text-lg md:text-2xl lg:text-[34px] font-black text-[#009A63] leading-snug text-center"
                style={{
                  WebkitTextStroke: "0.8px rgba(0,0,0,0.35)",
                  textShadow: "0 1px 3px rgba(0,0,0,0.15), 0 0 1px rgba(0,0,0,0.2)",
                }}
              >
                <span className="whitespace-nowrap block sm:inline">Hiệp Hội Công Nghiệp</span>
                <span className="whitespace-nowrap block sm:inline sm:before:content-['_']">Carbon Nano Hàn Quốc</span>
              </p>
              <p className="text-[10px] sm:text-sm md:text-lg lg:text-xl font-semibold text-black leading-snug mt-0.5 text-center">
                <span className="whitespace-nowrap block sm:inline">Nền tảng B2B, kết nối</span>
                <span className="whitespace-nowrap block sm:inline sm:before:content-['_']">doanh nghiệp Hàn - Việt</span>
              </p>
            </div>

            {/* Right actions */}
            <div className="flex items-center gap-3 flex-shrink-0 z-20">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 text-gray-600 cursor-pointer bg-white/80 rounded-lg hover:bg-gray-100"
              >
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className={`${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`} />
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Navigation Tabs Row */}
        <div className="hidden lg:block bg-white">
          <div className="max-w-[1400px] mx-auto px-6">
            <div className="flex items-stretch">
              {mainNavItems.map((item) => (
                <div
                  key={item.id}
                  className="relative"
                  onMouseEnter={
                    item.flyoutType === "categories"
                      ? openFlyout
                      : item.flyoutType === "companies"
                      ? openCompanyFlyout
                      : item.flyoutType === "activities"
                      ? openActivitiesFlyout
                      : item.flyoutType === "intro"
                      ? openIntroFlyout
                      : undefined
                  }
                  onMouseLeave={
                    item.flyoutType === "categories"
                      ? closeFlyout
                      : item.flyoutType === "companies"
                      ? closeCompanyFlyout
                      : item.flyoutType === "activities"
                      ? closeActivitiesFlyout
                      : item.flyoutType === "intro"
                      ? closeIntroFlyout
                      : undefined
                  }
                >
                  <Link
                    to={item.href}
                    className={`flex items-center gap-1.5 px-4 py-3.5 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap border-b-2 ${
                      item.isHighlight
                        ? "text-[#00AEEF] border-transparent hover:border-[#00AEEF]"
                        : (flyoutOpen && item.flyoutType === "categories") || (companyFlyoutOpen && item.flyoutType === "companies") || (activitiesFlyoutOpen && item.flyoutType === "activities") || (introFlyoutOpen && item.flyoutType === "intro")
                        ? "text-[#004A99] border-[#004A99]"
                        : "text-[#212529] border-transparent hover:text-[#004A99] hover:border-[#004A99]"
                    }`}
                  >
                    {item.hasFlyout && item.id !== "intro" && item.id !== "new-products" && item.id !== "companies" && (
                      <div className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className={item.flyoutType === "companies" ? "ri-building-2-line text-xs" : item.flyoutType === "activities" ? "ri-calendar-event-line text-xs" : item.flyoutType === "intro" ? "ri-hand-heart-line text-xs" : "ri-grid-line text-xs"} />
                      </div>
                    )}
                    <span>{item.label}</span>
                    {item.hasFlyout && (
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i
                          className={`ri-arrow-down-s-line text-xs transition-transform ${
                            (flyoutOpen && item.flyoutType === "categories") || (companyFlyoutOpen && item.flyoutType === "companies") || (activitiesFlyoutOpen && item.flyoutType === "activities") || (introFlyoutOpen && item.flyoutType === "intro")
                              ? "rotate-180"
                              : ""
                          }`}
                        />
                      </div>
                    )}
                    {item.id === "korea-brief" && (
                      <span className="ml-0.5 text-[9px] bg-[#00AEEF] text-white px-1 py-0.5 rounded font-bold">NEW</span>
                    )}
                  </Link>
                </div>
              ))}

              {/* Search Icon + Dropdown */}
              <div
                className="relative flex items-center ml-auto"
                onMouseEnter={() => setSearchOpen(true)}
                onMouseLeave={() => setSearchOpen(false)}
                ref={searchDropdownRef}
              >
                <button
                  type="button"
                  className={`flex items-center gap-1.5 px-4 py-3.5 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap border-b-2 ${
                    searchOpen
                      ? "text-[#004A99] border-[#004A99]"
                      : "text-[#212529] border-transparent hover:text-[#004A99] hover:border-[#004A99]"
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-search-line text-sm" />
                  </div>
                  <span className="hidden xl:inline">Tìm kiếm</span>
                </button>
                {searchOpen && (
                  <div className="absolute top-full right-0 mt-0 bg-white border border-gray-200 shadow-2xl z-[200] rounded-b-xl rounded-tl-xl overflow-hidden w-80">
                    <form onSubmit={handleSearch} className="p-3">
                      <div className="flex w-full h-10 rounded-lg border border-gray-200 focus-within:border-[#004A99] focus-within:ring-1 focus-within:ring-[#004A99]/30 transition-all bg-white">
                        <div ref={searchTypeRef} className="relative flex-shrink-0">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setSearchTypeOpen(!searchTypeOpen); }}
                            className="h-full flex items-center gap-1.5 px-3 bg-[#F8F9FA] border-r border-gray-200 text-xs font-semibold text-[#004A99] hover:bg-[#004A99]/5 transition-colors cursor-pointer whitespace-nowrap rounded-l-lg"
                          >
                            <span className="text-sm leading-none">{currentSearchType.emoji}</span>
                            <span>{currentSearchType.label}</span>
                            <div className="w-3 h-3 flex items-center justify-center">
                              <i className={`ri-arrow-down-s-line text-xs transition-transform ${searchTypeOpen ? "rotate-180" : ""}`} />
                            </div>
                          </button>
                          {searchTypeOpen && (
                            <div className="absolute top-full left-0 mt-1.5 bg-white border border-gray-200 rounded-xl shadow-xl z-[200] min-w-[130px] py-1.5 overflow-hidden">
                              {searchTypes.map((t) => (
                                <button
                                  key={t.value}
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); setSearchType(t.value); setSearchTypeOpen(false); }}
                                  className={`w-full text-left flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                                    searchType === t.value
                                      ? "bg-[#004A99] text-white font-semibold"
                                      : "text-[#212529] hover:bg-[#F8F9FA] hover:text-[#004A99]"
                                  }`}
                                >
                                  <span>{t.emoji}</span>
                                  <span>{t.label}</span>
                                  {searchType === t.value && (
                                    <div className="ml-auto w-4 h-4 flex items-center justify-center">
                                      <i className="ri-check-line text-xs" />
                                    </div>
                                  )}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder={
                            searchType === "product"
                              ? "Tìm kiếm sản phẩm..."
                              : searchType === "company"
                              ? "Tìm kiếm công ty..."
                              : "Tìm kiếm sản phẩm, công ty..."
                          }
                          className="flex-1 px-3 text-sm text-[#212529] placeholder-gray-400 outline-none bg-transparent min-w-0"
                        />
                        <button
                          type="submit"
                          className="flex-shrink-0 w-10 h-full bg-[#004A99] hover:bg-[#003d7d] text-white flex items-center justify-center transition-colors cursor-pointer rounded-r-lg"
                        >
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-search-line text-sm" />
                          </div>
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 py-2">
            {/* Mobile Search */}
            <form onSubmit={handleSearch} className="px-4 mb-2">
              <div className="flex w-full h-10 rounded-lg border border-gray-200 focus-within:border-[#004A99] focus-within:ring-1 focus-within:ring-[#004A99]/30 transition-all bg-white">
                <div className="relative flex-shrink-0">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setSearchTypeOpen(!searchTypeOpen); }}
                    className="h-full flex items-center gap-1.5 px-3 bg-[#F8F9FA] border-r border-gray-200 text-xs font-semibold text-[#004A99] hover:bg-[#004A99]/5 transition-colors cursor-pointer whitespace-nowrap rounded-l-lg"
                  >
                    <span>{currentSearchType.emoji}</span>
                    <span>{currentSearchType.label}</span>
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className={`ri-arrow-down-s-line text-xs transition-transform ${searchTypeOpen ? "rotate-180" : ""}`} />
                    </div>
                  </button>
                  {searchTypeOpen && (
                    <div className="absolute top-full left-0 mt-1.5 bg-white border border-gray-200 rounded-xl shadow-xl z-[200] min-w-[130px] py-1.5 overflow-hidden">
                      {searchTypes.map((t) => (
                        <button
                          key={t.value}
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setSearchType(t.value); setSearchTypeOpen(false); }}
                          className={`w-full text-left flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                            searchType === t.value
                              ? "bg-[#004A99] text-white font-semibold"
                              : "text-[#212529] hover:bg-[#F8F9FA] hover:text-[#004A99]"
                          }`}
                        >
                          <span>{t.emoji}</span>
                          <span>{t.label}</span>
                          {searchType === t.value && (
                            <div className="ml-auto w-4 h-4 flex items-center justify-center">
                              <i className="ri-check-line text-xs" />
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Tìm kiếm..."
                  className="flex-1 px-3 text-sm text-[#212529] placeholder-gray-400 outline-none bg-transparent min-w-0"
                />
                <button
                  type="submit"
                  className="flex-shrink-0 w-10 h-full bg-[#004A99] hover:bg-[#003d7d] text-white flex items-center justify-center transition-colors cursor-pointer rounded-r-lg"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-search-line text-sm" />
                  </div>
                </button>
              </div>
            </form>
            {mainNavItems.map((item) => (
              item.flyoutType === "intro" ? (
                <div key={item.id}>
                  <button
                    onClick={() => setMobileIntroAccordion(!mobileIntroAccordion)}
                    className="w-full flex items-center justify-between px-5 py-2 text-[13px] text-[#212529] hover:bg-[#004A99]/5 hover:text-[#004A99] cursor-pointer border-b border-gray-50"
                  >
                    <span>{item.label}</span>
                    <i className={`ri-arrow-down-s-line text-xs transition-transform ${mobileIntroAccordion ? "rotate-180" : ""}`} />
                  </button>
                  {mobileIntroAccordion && (
                    <div className="bg-[#F8F9FA] border-b border-gray-100">
                      <Link to="/loi-chao" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 px-6 py-1.5 text-[11px] text-[#212529] hover:bg-[#004A99]/5 hover:text-[#004A99] cursor-pointer border-b border-gray-50">
                        Lời chào
                      </Link>
                      <Link to="/khai-quat-cong-ty" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 px-6 py-1.5 text-[11px] text-[#212529] hover:bg-[#004A99]/5 hover:text-[#004A99] cursor-pointer">
                        Khái quát công ty
                      </Link>
                    </div>
                  )}
                </div>
              ) : item.flyoutType === "companies" ? (
                <div key={item.id}>
                  <button
                    onClick={() => {
                      const isOpen = mobileCompanyAccordion !== null;
                      if (!isOpen) {
                        setMobileCompanyAccordion("open");
                        categories.forEach((cat) => fetchCompaniesByCategory(cat.id, cat.name));
                      } else {
                        setMobileCompanyAccordion(null);
                      }
                    }}
                    className="w-full flex items-center justify-between px-5 py-2 text-[13px] text-[#212529] hover:bg-[#004A99]/5 hover:text-[#004A99] cursor-pointer border-b border-gray-50"
                  >
                    <span className="flex items-center gap-2">
                      {item.label}
                    </span>
                    <i className={`ri-arrow-down-s-line text-xs transition-transform ${mobileCompanyAccordion ? "rotate-180" : ""}`} />
                  </button>
                  {mobileCompanyAccordion && (
                    <div className="bg-[#F8F9FA] border-b border-gray-100">
                      <Link
                        to="/doanh-nghiep"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-2 px-6 py-1.5 text-[11px] font-semibold text-[#004A99] hover:bg-[#004A99]/5 cursor-pointer border-b border-gray-100"
                      >
                        <i className="ri-building-2-line text-[11px]" />
                        Tất cả doanh nghiệp
                      </Link>
                      {categories.map((cat) => {
                        const catCompanies = companiesByCategory[cat.id] ?? [];
                        return (
                          <div key={cat.id}>
                            <button
                              onClick={() => {
                                setMobileCompanyAccordion(mobileCompanyAccordion === cat.id ? "open" : cat.id);
                                fetchCompaniesByCategory(cat.id, cat.name);
                              }}
                              className="w-full flex items-center justify-between px-6 py-1.5 text-[11px] font-semibold text-[#004A99] hover:bg-[#004A99]/5 cursor-pointer"
                            >
                              <span>{cat.name}</span>
                              <i className={`ri-arrow-down-s-line text-[10px] transition-transform ${mobileCompanyAccordion === cat.id ? "rotate-180" : ""}`} />
                            </button>
                            {mobileCompanyAccordion === cat.id && catCompanies.length > 0 && (
                              <div className="pb-1">
                                {catCompanies.slice(0, 5).map((c) => (
                                  <Link
                                    key={c.company_name}
                                    to={c.company_id ? `/doanh-nghiep/${c.company_id}` : "/doanh-nghiep"}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="flex items-center gap-3 px-8 py-1.5 text-[11px] text-gray-600 hover:text-[#004A99] cursor-pointer"
                                  >
                                    <div className="w-5 h-5 rounded-lg bg-[#004A99]/10 flex items-center justify-center flex-shrink-0">
                                      {c.company_logo ? <img src={c.company_logo} alt="" className="w-full h-full object-contain rounded-lg" /> : <i className="ri-building-2-line text-[#004A99] text-[9px]" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="font-semibold truncate">{c.company_name}</p>
                                      <p className="text-[9px] text-gray-400 truncate">{c.featured_product_name}</p>
                                    </div>
                                  </Link>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : item.flyoutType === "categories" ? (
                <div key={item.id}>
                  <button
                    onClick={() => setMobileProductAccordion(mobileProductAccordion ? null : "open")}
                    className="w-full flex items-center justify-between px-5 py-2 text-[13px] text-[#212529] hover:bg-[#004A99]/5 hover:text-[#004A99] cursor-pointer border-b border-gray-50"
                  >
                    <span className="flex items-center gap-2">
                      {item.label}
                    </span>
                    <i className={`ri-arrow-down-s-line text-xs transition-transform ${mobileProductAccordion ? "rotate-180" : ""}`} />
                  </button>
                  {mobileProductAccordion && (
                    <div className="bg-[#F8F9FA] border-b border-gray-100">
                      <Link
                        to="/danh-muc"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-2 px-6 py-1.5 text-[11px] font-semibold text-[#004A99] hover:bg-[#004A99]/5 cursor-pointer border-b border-gray-100"
                      >
                        <i className="ri-apps-line text-[11px]" />
                        Tất cả sản phẩm
                      </Link>
                      {categories.map((cat) => {
                        const isExpanded = mobileProductAccordion === cat.id;
                        return (
                          <div key={cat.id}>
                            <button
                              onClick={() => setMobileProductAccordion(isExpanded ? "open" : cat.id)}
                              className="w-full flex items-center justify-between px-6 py-1.5 text-[11px] font-semibold text-[#004A99] hover:bg-[#004A99]/5 cursor-pointer"
                            >
                              <span className="flex items-center gap-2">
                                <span className="w-4 h-4 rounded-full bg-[#004A99]/10 text-[#004A99] text-[9px] font-bold flex items-center justify-center flex-shrink-0">{cat.code}</span>
                                {cat.name}
                              </span>
                              <i className={`ri-arrow-down-s-line text-[10px] transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                            </button>
                            {isExpanded && (
                              <div className="pb-1">
                                {cat.subCategories.map((sub) => (
                                  <Link
                                    key={sub.id}
                                    to={`/danh-muc/${cat.id}?sub=${sub.id}`}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="flex items-center gap-2 px-8 py-1.5 text-[11px] text-gray-600 hover:text-[#004A99] cursor-pointer"
                                  >
                                    <div className="w-1.5 h-1.5 rounded-full bg-[#00AEEF] flex-shrink-0" />
                                    {sub.name}
                                  </Link>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.id}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center justify-between px-5 py-2 text-[13px] text-[#212529] hover:bg-[#004A99]/5 hover:text-[#004A99] cursor-pointer border-b border-gray-50"
                >
                  <span className={item.isHighlight ? "text-[#00AEEF] font-semibold" : ""}>{item.label}</span>
                </Link>
              )
            ))}
            <div className="px-5 pt-2 border-t border-gray-100 mt-1.5 flex gap-2 flex-wrap">
              <Link to="/tra-cuu-tu-van" onClick={() => setMobileMenuOpen(false)} className="text-[13px] text-[#004A99] font-semibold cursor-pointer whitespace-nowrap flex items-center gap-1">
                <i className="ri-search-eye-line text-sm" />
                Tra Cứu Tư Vấn
              </Link>
            </div>
          </div>
        )}

      </nav>

      {/* ── FLYOUT MEGA MENU: CATEGORIES (3-level) ── */}
      {flyoutOpen && (
        <div
          className="fixed left-0 right-0 z-30 bg-white shadow-2xl"
          style={{ top: "calc(88px + 46px)", borderTop: "3px solid #004A99" }}
          onMouseEnter={keepFlyout}
          onMouseLeave={closeFlyout}
        >
          <div className="max-w-[1400px] mx-auto">
            <div className="flex">
              {/* Col 1: Parent Categories */}
              <div className="w-64 border-r border-gray-100 bg-[#F8F9FA] flex-shrink-0 py-4">
                <div className="px-4 pb-2 mb-1 border-b border-gray-200">
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                    Danh Mục Sản Phẩm
                  </p>
                </div>
                {categories.map((cat) => (
                  <div
                    key={cat.id}
                    onMouseEnter={() => handleParentHover(cat.id, cat.name)}
                    className={`flex items-center justify-between px-4 py-2.5 cursor-pointer transition-all group ${
                      hoveredSubId === cat.id
                        ? "bg-[#004A99] text-white"
                        : "text-[#212529] hover:bg-[#004A99]/8 hover:text-[#004A99]"
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <span
                        className={`text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                          hoveredSubId === cat.id
                            ? "bg-white/20 text-white"
                            : "bg-[#004A99]/10 text-[#004A99]"
                        }`}
                      >
                        {cat.code}
                      </span>
                      <p className="text-xs font-semibold whitespace-nowrap">{catName(cat)}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      {(() => {
                        const byId = categoryCounts[cat.id];
                        const byName = categoryCounts[`name:${cat.name.toLowerCase()}`];
                        const count = byId ?? byName;
                        if (!count || count === 0) return null;
                        return (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap ${
                            hoveredSubId === cat.id ? "bg-white/20 text-white" : "bg-gray-200 text-gray-500"
                          }`}>
                            {count.toLocaleString()}
                          </span>
                        );
                      })()}
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-arrow-right-s-line text-xs" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Col 2: Sub-categories */}
              <div className="w-60 border-r border-gray-100 flex-shrink-0 py-4">
                {hoveredCategory ? (
                  <>
                    <div className="px-4 pb-2 mb-1 border-b border-gray-200 flex items-center gap-2">
                      <span className="text-[10px] font-bold text-[#004A99] uppercase tracking-wider whitespace-nowrap">
                        {catName(hoveredCategory)}
                      </span>
                    </div>
                    <div className="overflow-y-auto" style={{ maxHeight: "340px" }}>
                      {hoveredCategory.subCategories.map((sub) => (
                        <div
                          key={sub.id}
                          onMouseEnter={() => handleSubCatEnter(sub.id, sub.name)}
                          onMouseLeave={handleSubCatLeave}
                          className={`flex items-center justify-between px-4 py-2.5 cursor-pointer transition-all ${
                            hoveredSubCatId === sub.id
                              ? "bg-[#004A99]/8 text-[#004A99]"
                              : "text-[#212529] hover:bg-[#004A99]/5 hover:text-[#004A99]"
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <div
                              className={`w-1.5 h-1.5 rounded-full flex-shrink-0 transition-colors ${
                                hoveredSubCatId === sub.id ? "bg-[#004A99]" : "bg-[#00AEEF]"
                              }`}
                            />
                            <span className="text-xs font-medium whitespace-nowrap">
                              {sub.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <span className="text-[10px] text-gray-400 whitespace-nowrap">
                              {categoryCounts[sub.id] != null ? categoryCounts[sub.id].toLocaleString() : ""}
                            </span>
                            {sub.subSubCategories && sub.subSubCategories.length > 0 && (
                              <div className="w-3 h-3 flex items-center justify-center">
                                <i className="ri-arrow-right-s-line text-xs text-gray-400" />
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="px-4 pt-3 border-t border-gray-100 mt-2">
                      <Link
                        to={`/danh-muc/${hoveredCategory.id}`}
                        className="text-xs text-[#004A99] font-semibold flex items-center gap-1 hover:underline cursor-pointer whitespace-nowrap"
                      >
                        Xem tất cả {(categoryCounts[hoveredCategory.id] ?? categoryCounts[`name:${hoveredCategory.name.toLowerCase()}`]) ? `(${(categoryCounts[hoveredCategory.id] ?? categoryCounts[`name:${hoveredCategory.name.toLowerCase()}`]).toLocaleString()})` : ""}
                        <i className="ri-arrow-right-line text-xs" />
                      </Link>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center justify-center h-32">
                    <p className="text-xs text-gray-400">
                      Chọn danh mục
                    </p>
                  </div>
                )}
              </div>

              {/* Col 3: Products from sub_category (or parent category fallback) */}
              <div className="flex-1 py-4 px-5">
                {(() => {
                  // Priority: sub-category products > parent category products
                  const activeId = hoveredSubCatId || hoveredSubId;
                  const activeProducts = activeId ? (categoryProducts[activeId] ?? null) : null;
                  const activeLabel = hoveredSubCat
                    ? hoveredSubCat.name
                    : hoveredCategory
                    ? hoveredCategory.name
                    : "";
                  const linkTarget = hoveredSubId
                    ? `/danh-muc/${hoveredSubId}${hoveredSubCatId ? `?sub=${hoveredSubCatId}` : ""}`
                    : "/danh-muc";

                  if (!activeId) {
                    return (
                      <div className="flex flex-col items-center justify-center h-full text-center">
                        <div className="w-12 h-12 rounded-full bg-[#004A99]/5 flex items-center justify-center mb-3">
                          <i className="ri-cursor-line text-xl text-[#004A99]/30" />
                        </div>
                        <p className="text-xs text-gray-400 max-w-[180px] leading-relaxed">
                          Rê chuột vào danh mục để xem sản phẩm
                        </p>
                      </div>
                    );
                  }

                  if (activeProducts === null) {
                    return (
                      <div className="flex flex-col items-center justify-center h-full text-center">
                        <div className="w-12 h-12 rounded-full bg-[#004A99]/5 flex items-center justify-center mb-3">
                          <i className="ri-loader-4-line text-xl text-[#004A99]/30 animate-spin" />
                        </div>
                        <p className="text-xs text-gray-400 max-w-[180px] leading-relaxed">
                          Đang tải sản phẩm...
                        </p>
                      </div>
                    );
                  }

                  if (activeProducts.length === 0) {
                    return (
                      <div className="flex flex-col items-center justify-center h-full text-center">
                        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                          <i className="ri-inbox-line text-xl text-gray-300" />
                        </div>
                        <p className="text-xs text-gray-400 max-w-[180px] leading-relaxed">
                          Chưa có sản phẩm trong danh mục này
                        </p>
                        <Link to="/danh-muc" className="mt-2 text-[10px] text-[#004A99] font-medium hover:underline whitespace-nowrap">
                          Xem tất cả sản phẩm →
                        </Link>
                      </div>
                    );
                  }

                  return (
                    <>
                      <div className="pb-2 mb-3 border-b border-gray-200 flex items-center justify-between">
                        <p className="text-[10px] font-bold text-[#004A99] uppercase tracking-wider truncate max-w-[160px]">
                          {activeLabel || "Sản Phẩm Mới Nhất"}
                        </p>
                        <Link to={linkTarget} className="text-[10px] text-[#00AEEF] font-medium hover:underline whitespace-nowrap flex-shrink-0">
                          Xem tất cả →
                        </Link>
                      </div>
                      <div className="space-y-3">
                        {activeProducts.slice(0, 3).map((product) => (
                          <Link
                            key={product.id}
                            to={getProductUrl({ companyName: product.company_name ?? "", name: product.name })}
                            className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-[#004A99]/5 transition-colors cursor-pointer group border border-transparent hover:border-[#004A99]/10"
                          >
                            <div className="w-14 h-14 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
                              {(() => {
                                const rawMedia = (product as unknown as Record<string, unknown>).media_items;
                                const mediaItems: Array<{ url: string; type: string; is_cover?: boolean }> = Array.isArray(rawMedia)
                                  ? rawMedia as Array<{ url: string; type: string; is_cover?: boolean }>
                                  : [];
                                const coverImg = mediaItems.find((m) => m.is_cover && m.type === "image");
                                const firstImg = mediaItems.find((m) => m.type === "image");
                                const thumbUrl = coverImg?.url ?? firstImg?.url ?? (product.images?.[0] ?? "");
                                return thumbUrl ? (
                                  <img
                                    src={thumbUrl}
                                    alt={product.name}
                                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center bg-[#004A99]/5">
                                    <i className="ri-box-3-line text-[#004A99]/40 text-xl" />
                                  </div>
                                );
                              })()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-gray-800 line-clamp-2 group-hover:text-[#004A99] transition-colors leading-tight">
                                {product.name}
                              </p>
                              {product.company_name && (
                                <div className="flex items-center gap-1 mt-1">
                                  {product.company_logo ? (
                                    <img src={product.company_logo} alt={product.company_name} className="w-4 h-4 rounded object-contain" />
                                  ) : (
                                    <div className="w-4 h-4 rounded bg-[#004A99]/10 flex items-center justify-center flex-shrink-0">
                                      <i className="ri-building-2-line text-[8px] text-[#004A99]" />
                                    </div>
                                  )}
                                  <span className="text-[10px] text-gray-400 truncate">{product.company_name}</span>
                                </div>
                              )}
                            </div>
                            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                              <i className="ri-arrow-right-s-line text-xs text-gray-300 group-hover:text-[#004A99]" />
                            </div>
                          </Link>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Banner panel */}
              <div className="w-48 border-l border-gray-100 p-4 flex-shrink-0 bg-gradient-to-b from-[#004A99]/5 to-white">
                <p className="text-[10px] font-bold text-[#004A99] mb-3 uppercase tracking-wide">
                  Nổi Bật
                </p>
                <div className="space-y-3">
                  <div className="rounded-lg overflow-hidden cursor-pointer group">
                    <img
                      src="https://readdy.ai/api/search-image?query=Korean%20carbon%20nano%20technology%20B2B%20expo%20trade%20show%20banner%20industrial%20innovation%20professional%20company%20introduction%20Vietnam%20market%20modern%20clean%20design&width=200&height=110&seq=flyout_banner_primary&orientation=landscape"
                      alt="Korean Companies"
                      className="w-full h-24 object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="bg-[#004A99] text-white text-xs p-2 text-center font-medium whitespace-nowrap">
                      Giới Thiệu DN →
                    </div>
                  </div>
                  <div className="bg-[#00AEEF]/10 rounded-lg border border-[#00AEEF]/30 p-3">
                    <p className="text-[10px] font-bold text-[#004A99] mb-1">
                      📋 Korean Brief
                    </p>
                    <p className="text-[10px] text-gray-500">
                      Báo cáo xu hướng công nghệ HQ
                    </p>
                    <Link
                      to="/korean-brief"
                      className="mt-2 text-[10px] text-[#00AEEF] font-medium flex items-center gap-1 cursor-pointer whitespace-nowrap"
                    >
                      Đọc ngay
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-arrow-right-line text-[10px]" />
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── FLYOUT MEGA MENU: COMPANIES (3-level mirroring categories) ── */}
      {companyFlyoutOpen && (
        <div
          className="fixed left-0 right-0 z-30 bg-white shadow-2xl"
          style={{ top: "calc(88px + 46px)", borderTop: "3px solid #004A99" }}
          onMouseEnter={keepCompanyFlyout}
          onMouseLeave={closeCompanyFlyout}
        >
          <div className="max-w-[1400px] mx-auto">
            <div className="flex">
              {/* Col 1: Category list (same as products flyout) */}
              <div className="w-64 border-r border-gray-100 bg-[#F8F9FA] flex-shrink-0 py-4">
                <div className="px-4 pb-2 mb-1 border-b border-gray-200">
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                    Danh Mục Ngành Nghề
                  </p>
                </div>
                {categories.map((cat) => {
                  const companyCount =
                    categoryCounts[`company:${cat.id}`] ??
                    categoryCounts[`company:name:${cat.name.toLowerCase()}`] ??
                    categoryCounts[`company:name:${cat.id}`];
                  return (
                  <div
                    key={cat.id}
                    onMouseEnter={() => handleCompanyCatHover(cat.id, cat.name)}
                    className={`flex items-center justify-between px-4 py-2.5 cursor-pointer transition-all group ${
                      companyCatId === cat.id
                        ? "bg-[#004A99] text-white"
                        : "text-[#212529] hover:bg-[#004A99]/8 hover:text-[#004A99]"
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <span
                        className={`text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                          companyCatId === cat.id
                            ? "bg-white/20 text-white"
                            : "bg-[#004A99]/10 text-[#004A99]"
                        }`}
                      >
                        {cat.code}
                      </span>
                      <p className="text-xs font-semibold whitespace-nowrap">{catName(cat)}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      {companyCount && companyCount > 0 ? (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap ${
                          companyCatId === cat.id ? "bg-white/20 text-white" : "bg-gray-200 text-gray-500"
                        }`}>
                          {companyCount}
                        </span>
                      ) : null}
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-arrow-right-s-line text-xs" />
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>

              {/* Col 2: Sub-categories with company counts */}
              <div className="w-56 border-r border-gray-100 flex-shrink-0 py-4">
                {companyCat ? (
                  <>
                    <div className="px-4 pb-2 mb-1 border-b border-gray-200">
                      <span className="text-[10px] font-bold text-[#004A99] uppercase tracking-wider whitespace-nowrap">
                        {catName(companyCat)}
                      </span>
                    </div>
                    <div className="overflow-y-auto" style={{ maxHeight: "340px" }}>
                      {companyCat.subCategories.map((sub) => {
                        // Count companies whose products belong to this sub-category
                        const subCompanySet = new Set<string>();
                        (supabaseCompanies ?? []).forEach((c) => {
                          c.all_products.forEach((p) => {
                            // match by sub category name in product name (rough match)
                            if (p.product_name && sub.name) subCompanySet.add(c.company_name);
                          });
                        });
                        // Use categoryCounts for sub-category product count
                        const subCount = categoryCounts[sub.id];
                        return (
                          <div
                            key={sub.id}
                            onMouseEnter={() => handleCompanySubCatEnter(sub.id)}
                            onMouseLeave={handleCompanySubCatLeave}
                            className={`flex items-center justify-between px-4 py-2.5 cursor-pointer transition-all ${
                              companySubCatId === sub.id
                                ? "bg-[#004A99]/8 text-[#004A99]"
                                : "text-[#212529] hover:bg-[#004A99]/5 hover:text-[#004A99]"
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <div
                                className={`w-1.5 h-1.5 rounded-full flex-shrink-0 transition-colors ${
                                  companySubCatId === sub.id ? "bg-[#004A99]" : "bg-[#00AEEF]"
                                }`}
                              />
                              <span className="text-xs font-medium whitespace-nowrap">
                                {sub.name}
                              </span>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {subCount != null && subCount > 0 && (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap ${
                                  companySubCatId === sub.id ? "bg-[#004A99]/10 text-[#004A99]" : "bg-gray-100 text-gray-500"
                                }`}>
                                  {subCount}
                                </span>
                              )}
                              {sub.subSubCategories && sub.subSubCategories.length > 0 && (
                                <div className="w-3 h-3 flex items-center justify-center">
                                  <i className="ri-arrow-right-s-line text-xs text-gray-400" />
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <div className="flex items-center justify-center h-32">
                    <p className="text-xs text-gray-400">
                      Chọn danh mục
                    </p>
                  </div>
                )}
              </div>

              {/* Col 3: Companies from Supabase (dynamic, conditional rendering) */}
              <div className="flex-1 py-4 px-5 min-w-0">
                <div className="pb-2 mb-3 border-b border-gray-200 flex items-center justify-between">
                  <p className="text-[10px] font-bold text-[#004A99] uppercase tracking-wider">
                    {companyCat ? catName(companyCat) : "Doanh Nghiệp Hàn Quốc"}
                  </p>
                  <Link to="/doanh-nghiep" className="text-[10px] text-[#00AEEF] font-medium hover:underline whitespace-nowrap">
                    Xem tất cả →
                  </Link>
                </div>

                {/* Loading state: category not yet fetched */}
                {supabaseCompanies === null && companyCatId ? (
                  <div className="flex flex-col items-center justify-center h-32 text-center">
                    <i className="ri-loader-4-line text-xl text-[#004A99]/40 animate-spin mb-2" />
                    <p className="text-xs text-gray-400">Đang tải...</p>
                  </div>

                ) : supabaseCompanies && supabaseCompanies.length > 0 ? (
                  /* Has companies from Supabase → each entry already represents 1 unique company with all_products list */
                  <div className="space-y-3 overflow-y-auto" style={{ maxHeight: "340px" }}>
                    {supabaseCompanies.slice(0, 5).map((company) => (
                      <div key={company.company_id || company.company_name} className="rounded-xl border border-gray-100 hover:border-[#004A99]/20 transition-all overflow-hidden">
                        {/* Company header — logo + name (only ONCE per company) */}
                        <Link
                          to={company.company_id ? `/doanh-nghiep/${company.company_id}` : "/doanh-nghiep"}
                          className="flex items-center gap-2.5 p-2.5 bg-[#F8F9FA] hover:bg-[#EEF4FF] transition-colors cursor-pointer group"
                        >
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden bg-white border border-gray-200 flex-shrink-0">
                            {company.company_logo ? (
                              <img src={company.company_logo} alt={company.company_name} className="w-full h-full object-contain p-0.5" />
                            ) : (
                              <i className="ri-building-2-line text-[#004A99] text-xs" />
                            )}
                          </div>
                          <p className="text-xs font-bold text-[#212529] group-hover:text-[#004A99] transition-colors truncate flex-1">{company.company_name}</p>
                          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                            <i className="ri-arrow-right-s-line text-xs text-gray-400 group-hover:text-[#004A99]" />
                          </div>
                        </Link>
                        {/* All products of this company — max 5 */}
                        {company.all_products && company.all_products.length > 0 && (
                          <div className="px-2.5 pb-2 pt-1 space-y-0.5">
                            {company.all_products.slice(0, 5).map((p) => (
                              <Link
                                key={p.product_id}
                                to={getProductUrl({ companyName: p.company_name ?? "", name: p.product_name })}
                                className="flex items-center gap-1.5 py-1 px-2 rounded-lg hover:bg-[#004A99]/5 transition-colors cursor-pointer group/item"
                              >
                                <div className="w-1 h-1 rounded-full bg-[#00AEEF] flex-shrink-0" />
                                <span className="text-[11px] text-gray-600 group-hover/item:text-[#004A99] transition-colors truncate leading-tight">
                                  {p.product_name}
                                </span>
                              </Link>
                            ))}
                            {company.all_products.length > 5 && (
                              <Link
                                to={company.company_id ? `/doanh-nghiep/${company.company_id}` : "/doanh-nghiep"}
                                className="flex items-center gap-1.5 py-1 px-2 rounded-lg hover:bg-[#004A99]/5 transition-colors cursor-pointer"
                              >
                                <div className="w-1 h-1 rounded-full bg-gray-300 flex-shrink-0" />
                                <span className="text-[11px] text-[#004A99] font-medium truncate leading-tight">
                                  +{company.all_products.length - 5} sản phẩm khác...
                                </span>
                              </Link>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                ) : supabaseCompanies && supabaseCompanies.length === 0 ? (
                  /* Conditional rendering: category has no products → show empty state, no dropdown */
                  <div className="flex flex-col items-center justify-center h-32 text-center">
                    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mb-2">
                      <i className="ri-building-2-line text-gray-300 text-lg" />
                    </div>
                    <p className="text-xs text-gray-400 leading-relaxed">
                      Chưa có doanh nghiệp trong danh mục này
                    </p>
                    <Link
                      to="/doanh-nghiep"
                      className="mt-2 text-[10px] text-[#004A99] font-medium hover:underline whitespace-nowrap"
                    >
                      Xem tất cả doanh nghiệp →
                    </Link>
                  </div>

                ) : (
                  /* Initial state: no category selected yet */
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <div className="w-12 h-12 rounded-full bg-[#004A99]/5 flex items-center justify-center mb-3">
                      <i className="ri-cursor-line text-xl text-[#004A99]/30" />
                    </div>
                    <p className="text-xs text-gray-400 max-w-[180px] leading-relaxed">
                      Rê chuột vào danh mục để xem doanh nghiệp
                    </p>
                  </div>
                )}
              </div>

              {/* Col 4: Right info panel */}
              <div className="w-48 border-l border-gray-100 p-4 flex-shrink-0 bg-gradient-to-b from-[#004A99]/5 to-white">
                <p className="text-[10px] font-bold text-[#004A99] mb-3 uppercase tracking-wide">
                  Lợi Ích Đối Tác
                </p>
                <div className="space-y-2.5">
                  {[
                    { icon: "ri-verified-badge-line", label: "Doanh nghiệp được xác minh" },
                    { icon: "ri-translate-2", label: "Hỗ trợ tiếng Việt" },
                    { icon: "ri-links-line", label: "Kết nối trực tiếp" },
                    { icon: "ri-customer-service-2-line", label: "Tư vấn song ngữ" },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-lg bg-[#004A99]/10 flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-[#004A99] text-xs`} />
                      </div>
                      <span className="text-[10px] text-gray-600 leading-tight">{item.label}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-3 bg-[#004A99] rounded-xl">
                  <p className="text-white text-[10px] font-bold mb-1">
                    Đăng Ký Kết Nối
                  </p>
                  <p className="text-white/70 text-[10px] mb-2 leading-tight">
                    Kết nối với DN Hàn Quốc ngay
                  </p>
                  <Link
                    to="/partner-apply"
                    className="block text-center bg-white text-[#004A99] text-[10px] font-bold px-2 py-1.5 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Đăng ký ngay
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}



      {/* Overlay backdrops */}
      {flyoutOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/10"
          style={{ top: "calc(88px + 46px)" }}
          onMouseEnter={closeFlyout}
        />
      )}
      {companyFlyoutOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/10"
          style={{ top: "calc(88px + 46px)" }}
          onMouseEnter={closeCompanyFlyout}
        />
      )}
      {introFlyoutOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/10"
          style={{ top: "calc(88px + 46px)" }}
          onMouseEnter={closeIntroFlyout}
        />
      )}

      {/* ── FLYOUT: GIỚI THIỆU ── */}
      {introFlyoutOpen && (
        <div
          className="fixed left-0 right-0 z-30 bg-white shadow-2xl"
          style={{ top: "calc(88px + 46px)", borderTop: "3px solid #004A99" }}
          onMouseEnter={keepIntroFlyout}
          onMouseLeave={closeIntroFlyout}
        >
          <div className="max-w-[1400px] mx-auto py-5 px-8">
            <div className="flex gap-6">
              {/* Menu items */}
              <div className="flex flex-col gap-2 min-w-[220px]">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Giới thiệu</p>
                <Link
                  to="/loi-chao"
                  onClick={() => setIntroFlyoutOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-[#004A99]/8 hover:text-[#004A99] text-[#212529] transition-colors cursor-pointer group"
                >
                  <div className="w-9 h-9 rounded-xl bg-[#004A99]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#004A99]/20 transition-colors">
                    <i className="ri-hand-heart-line text-[#004A99] text-base" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold">Lời chào</p>
                    <p className="text-[11px] text-gray-400">Thông điệp từ lãnh đạo Hiệp Hội</p>
                  </div>
                  <div className="ml-auto w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <i className="ri-arrow-right-s-line text-[#004A99] text-xs" />
                  </div>
                </Link>
                <Link
                  to="/khai-quat-cong-ty"
                  onClick={() => setIntroFlyoutOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-[#004A99]/8 hover:text-[#004A99] text-[#212529] transition-colors cursor-pointer group"
                >
                  <div className="w-9 h-9 rounded-xl bg-[#004A99]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#004A99]/20 transition-colors">
                    <i className="ri-building-2-line text-[#004A99] text-base" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold">Khái quát công ty</p>
                    <p className="text-[11px] text-gray-400">Tổng quan về Hiệp Hội Carbon Nano</p>
                  </div>
                  <div className="ml-auto w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <i className="ri-arrow-right-s-line text-[#004A99] text-xs" />
                  </div>
                </Link>
              </div>

              {/* Divider */}
              <div className="w-px bg-gray-100 self-stretch" />

              {/* Brief intro block */}
              <div className="flex-1 max-w-[320px]">
                <div className="bg-gradient-to-br from-[#004A99]/5 to-[#004A99]/5 rounded-2xl p-5 border border-[#004A99]/15 h-full flex flex-col justify-center">
                  <div className="w-10 h-10 rounded-xl bg-[#004A99] flex items-center justify-center mb-3">
                    <i className="ri-award-line text-white text-lg" />
                  </div>
                  <p className="text-sm font-bold text-gray-800 mb-1">Hiệp Hội Công Nghiệp Carbon Nano Hàn Quốc</p>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Kết nối doanh nghiệp Hàn – Việt, thúc đẩy hợp tác công nghệ nano carbon tiên tiến trong khu vực.
                  </p>
                  <Link
                    to="/khai-quat-cong-ty"
                    onClick={() => setIntroFlyoutOpen(false)}
                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#004A99] hover:underline cursor-pointer whitespace-nowrap"
                  >
                    Tìm hiểu thêm
                    <i className="ri-arrow-right-line text-xs" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Spacer: header(88) + nav(46) */}
      <div className="h-[134px]" />
      <style>{`
        @keyframes pulse-scale {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.08); }
        }
        .animate-pulse-scale {
          animation: pulse-scale 2s ease-in-out infinite;
        }
      `}</style>
    </>
  );
};

export default Navbar;
